import React from "react";
import { GridCellProps, GridColumn, GridRowProps } from "@progress/kendo-react-grid";
import { ChooserColumn, useColumnFit, useMedia } from "@axum/ax-design";
import { isBudget } from "../../../utils/orderDisplay";
import { useReportTableRoom } from "../../../config/tableRoom";
import {
  ClientCell, DateCell, MoneyCell, NumberCell, OrderCellProps, OriginCell, SellerCell, StateCell, TextCell, ToolsCell, ToolsHeader, TotalCell, TypeCell,
} from "./OrderCells";
import { SPLIT_MEDIA } from "./styles";

export const ORDER_COLUMNS: ChooserColumn[] = [
  { key: "id", title: "ID Axum", visible: true, fixed: true },
  { key: "erpOrderId", title: "ID ERP", visible: false },
  { key: "originOrderId", title: "ID Source", visible: false },
  { key: "orderDate", title: "Fecha", visible: true },
  { key: "originSystemCode", title: "Origen", visible: true },
  { key: "sellerName", title: "Vendedor", visible: true },
  { key: "clientName", title: "Cliente", visible: true },
  { key: "clientChannel", title: "Canal", visible: true },
  { key: "totalNetPrice", title: "Neto", visible: false },
  { key: "totalTaxes", title: "IVA + imp. internos", visible: false },
  { key: "totalPerceptions", title: "Percepciones", visible: false },
  { key: "total", title: "Monto", visible: true },
  { key: "erpState", title: "Estado", visible: true },
  { key: "invoiceId", title: "Factura", visible: false },
  { key: "axumOperationType", title: "Tipo", visible: true },
];

const CARD_ROLES: { [key: string]: string } = {
  clientName: "ax-datagrid-title",
  total: "ax-datagrid-end",
  erpState: "ax-datagrid-tag",
};

const titleOf = (key: string) => (ORDER_COLUMNS.find(column => column.key === key) || { title: "" }).title;

// KendoReact también dibuja las celdas de las columnas en las filas de grupo: la franja ya ocupa todo el ancho.
const orderCell = (key: string, Cell: React.ComponentType<OrderCellProps>) => {
  const label = CARD_ROLES[key] ? undefined : titleOf(key);
  return (props: GridCellProps) => (props.rowType === "groupHeader" ? null : <Cell {...props} label={label} />);
};

const COLUMN_CELLS: { [key: string]: { weight: number; min: number; cell: React.ComponentType<GridCellProps> } } = {
  id: { weight: 4, min: 5.5, cell: orderCell("id", NumberCell) },
  erpOrderId: { weight: 4, min: 5, cell: orderCell("erpOrderId", NumberCell) },
  originOrderId: { weight: 4, min: 6.5, cell: orderCell("originOrderId", TextCell) },
  orderDate: { weight: 5, min: 8.5, cell: orderCell("orderDate", DateCell) },
  originSystemCode: { weight: 3, min: 6.5, cell: orderCell("originSystemCode", OriginCell) },
  sellerName: { weight: 8, min: 10, cell: orderCell("sellerName", SellerCell) },
  clientName: { weight: 16, min: 10, cell: orderCell("clientName", ClientCell) },
  clientChannel: { weight: 4, min: 6.5, cell: orderCell("clientChannel", TextCell) },
  totalNetPrice: { weight: 4, min: 7.5, cell: orderCell("totalNetPrice", MoneyCell) },
  totalTaxes: { weight: 4, min: 7.5, cell: orderCell("totalTaxes", MoneyCell) },
  totalPerceptions: { weight: 4, min: 7.5, cell: orderCell("totalPerceptions", MoneyCell) },
  total: { weight: 5, min: 7.5, cell: orderCell("total", TotalCell) },
  erpState: { weight: 4, min: 11.5, cell: orderCell("erpState", StateCell) },
  invoiceId: { weight: 4, min: 9, cell: orderCell("invoiceId", TextCell) },
  axumOperationType: { weight: 3, min: 7, cell: orderCell("axumOperationType", TypeCell) },
};

// Sólo las que repite la burbuja: soltar otra perdería el dato.
const DROP_ORDER = [
  "axumOperationType", "clientChannel", "totalPerceptions", "totalTaxes", "totalNetPrice", "invoiceId", "originOrderId", "erpOrderId",
];

const CARD_LEAD = "axumOperationType";

// El ID no se pierde: la burbuja lo muestra junto al tipo.
const CARD_HIDDEN = ["id", ...DROP_ORDER.filter(key => key !== CARD_LEAD)];

const cellClass = (key: string) => {
  if (CARD_ROLES[key]) return CARD_ROLES[key];
  if (key === CARD_LEAD) return "o360-card-lead";
  return CARD_HIDDEN.indexOf(key) >= 0 ? "ax-datagrid-table-only" : undefined;
};

export const TOOLS_WIDTH = 126;
export const LEAD_WIDTH = 32;

export interface FittedColumn extends ChooserColumn {
  width?: number;
}

const isFactorySet = (columns: ChooserColumn[], factory: ChooserColumn[]) =>
  columns.every(column => factory.some(base => base.key === column.key && base.visible === column.visible));

// La columna que más pesa va sin ancho: con la tabla fija se queda con lo que sobra del redondeo.
// Lo que el usuario encendió en el selector no se suelta aunque no entre: sólo se aprieta.
// Decisión de producto: las tarjetas no tienen selector y muestran siempre todos los datos.
export const useOrderColumns = (host: React.RefObject<HTMLElement>, columns: ChooserColumn[], factory: ChooserColumn[], reserved: number): FittedColumn[] => {
  const cards = useMedia(SPLIT_MEDIA);
  const source = cards ? factory : columns;
  const visible = source.filter(column => column.visible);
  const factorySet = isFactorySet(source, factory);
  const fit = useColumnFit(host, {
    columns: visible.map(column => ({ key: column.key, weight: COLUMN_CELLS[column.key].weight, min: COLUMN_CELLS[column.key].min })),
    drop: factorySet ? DROP_ORDER : [],
    reserved,
  });
  useReportTableRoom(factorySet ? null : fit);
  const kept = visible.filter(column => fit.dropped.indexOf(column.key) < 0);
  const flex = kept.reduce<ChooserColumn | null>((widest, column) =>
    (!widest || COLUMN_CELLS[column.key].weight > COLUMN_CELLS[widest.key].weight ? column : widest), null);
  return kept.map(column => ({ ...column, width: column === flex ? undefined : fit.widths[column.key] }));
};

const ToolsDataCell = (props: GridCellProps) => (props.rowType === "groupHeader" ? null : <ToolsCell {...props} />);

export const renderOrderColumns = (columns: FittedColumn[]) => [
  ...columns.map(column => (
    <GridColumn
      key={column.key}
      field={column.key}
      title={column.title}
      width={column.width}
      className={cellClass(column.key)}
      cell={COLUMN_CELLS[column.key].cell}
    />
  )),
  <GridColumn
    key="tools"
    width={TOOLS_WIDTH}
    sortable={false}
    headerClassName="ax-table-tools-cell"
    className="ax-table-tools-cell"
    headerCell={ToolsHeader}
    cell={ToolsDataCell}
  />,
];

export const toneRow = (row: React.ReactElement<HTMLTableRowElement>, rowProps: GridRowProps) =>
  rowProps.rowType !== "data" ? row : React.cloneElement(row, {
    className: `${row.props.className || ""} ${isBudget(rowProps.dataItem) ? "ax-table-budget" : "ax-table-order"}`,
  });
